// Scheduling validation engine
// Enforces conflict detection, rest periods, and max hours

import { SEGMENT_TYPES, BLOCK_TYPES, getBlockWorkingHours, getBlockTimeSpan, validateSegmentContinuity } from './shiftTypes';

/**
 * Default configuration — can be overridden per organisation
 */
export const DEFAULT_SCHEDULING_CONFIG = {
  minRestHoursBetweenShifts: 11, // UK WTR default: 11 hours
  maxWeeklyHours: 48,            // UK WTR default: 48 hours
  maxDailyHours: 13,             // Practical max per day
  maxConsecutiveDays: 6,         // Max consecutive working days
  allowSplitShifts: true,
  enforceBreaks: true,
  breakDurationMinutes: 20,      // Required break after 6 hours
  breakThresholdHours: 6,
};

/**
 * Parse time string "HH:MM" to minutes from midnight
 */
export function timeToMinutes(timeStr) {
  if (!timeStr) return 0;
  const [h, m] = timeStr.split(':').map(Number);
  return h * 60 + (m || 0);
}

export function parseLocalDate(dateStr) {
  return new Date(`${dateStr}T00:00:00`);
}

export function formatLocalDate(date) {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
}

/**
 * Convert minutes from midnight to "HH:MM"
 */
export function minutesToTime(mins) {
  const h = Math.floor(mins / 60) % 24;
  const m = mins % 60;
  return `${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}`;
}

/**
 * Calculate shift duration in hours
 */
export function getShiftDurationHours(shift) {
  const start = timeToMinutes(shift.startTime);
  let end = timeToMinutes(shift.endTime);
  // Handle overnight shifts
  if (end <= start) end += 24 * 60;
  return (end - start) / 60;
}

/**
 * Check if two time ranges overlap (same day)
 */
export function timesOverlap(start1, end1, start2, end2) {
  const s1 = timeToMinutes(start1);
  let e1 = timeToMinutes(end1);
  const s2 = timeToMinutes(start2);
  let e2 = timeToMinutes(end2);
  if (e1 <= s1) e1 += 24 * 60;
  if (e2 <= s2) e2 += 24 * 60;
  return s1 < e2 && s2 < e1;
}

/**
 * Check for double-booking: does the staff member have a conflicting shift?
 * Returns { conflict: boolean, conflictingShift: object | null }
 */
export function checkDoubleBooking(staffId, newShift, existingShifts) {
  const conflicting = existingShifts.find(existing => {
    if (existing.id === newShift.id) return false; // Skip self
    if (existing.assignedStaffId !== staffId) return false;
    if (existing.date !== newShift.date) return false;
    if (existing.status === 'cancelled') return false;
    return timesOverlap(newShift.startTime, newShift.endTime, existing.startTime, existing.endTime);
  });

  return {
    conflict: !!conflicting,
    conflictingShift: conflicting || null,
  };
}

/**
 * Check rest period between shifts
 * Returns { valid: boolean, restHours: number, required: number }
 */
export function checkRestPeriod(staffId, newShift, existingShifts, config = DEFAULT_SCHEDULING_CONFIG) {
  const newDate = new Date(newShift.date);
  const newStartMin = timeToMinutes(newShift.startTime);

  // Check shifts from the day before and the same day
  const relevantShifts = existingShifts.filter(s => {
    if (s.id === newShift.id) return false;
    if (s.assignedStaffId !== staffId) return false;
    if (s.status === 'cancelled') return false;
    const sDate = new Date(s.date);
    const dayDiff = (newDate - sDate) / (1000 * 60 * 60 * 24);
    return dayDiff >= -1 && dayDiff <= 1;
  });

  let minRest = Infinity;

  for (const shift of relevantShifts) {
    const shiftDate = new Date(shift.date);
    const dayDiff = (newDate - shiftDate) / (1000 * 60 * 60 * 24);

    let shiftEndMin = timeToMinutes(shift.endTime);
    if (timeToMinutes(shift.endTime) <= timeToMinutes(shift.startTime)) {
      shiftEndMin += 24 * 60;
    }

    // Calculate rest in hours
    let restMinutes;
    if (dayDiff === 0) {
      // Same day — check gap between shifts
      if (newStartMin > shiftEndMin) {
        restMinutes = newStartMin - shiftEndMin;
      } else {
        const newEndMin = timeToMinutes(newShift.endTime) + (timeToMinutes(newShift.endTime) <= newStartMin ? 24 * 60 : 0);
        if (timeToMinutes(shift.startTime) > newEndMin) {
          restMinutes = timeToMinutes(shift.startTime) - newEndMin;
        } else {
          restMinutes = 0; // Overlapping
        }
      }
    } else if (dayDiff > 0) {
      // New shift is the next day
      restMinutes = (24 * 60 - shiftEndMin) + newStartMin + ((dayDiff - 1) * 24 * 60);
    } else {
      // New shift is the previous day
      const newEndMin = timeToMinutes(newShift.endTime) + (timeToMinutes(newShift.endTime) <= newStartMin ? 24 * 60 : 0);
      restMinutes = (24 * 60 - newEndMin) + timeToMinutes(shift.startTime) + ((-dayDiff - 1) * 24 * 60);
    }

    minRest = Math.min(minRest, restMinutes / 60);
  }

  if (minRest === Infinity) minRest = 24;

  return {
    valid: minRest >= config.minRestHoursBetweenShifts,
    restHours: Math.round(minRest * 10) / 10,
    required: config.minRestHoursBetweenShifts,
  };
}

/**
 * Calculate total weekly hours for a staff member
 * weekStart should be a Monday date string (YYYY-MM-DD)
 */
export function getWeeklyHours(staffId, weekStart, shifts) {
  const start = new Date(weekStart);
  const end = new Date(start);
  end.setDate(end.getDate() + 7);

  return shifts
    .filter(s => {
      if (s.assignedStaffId !== staffId) return false;
      if (s.status === 'cancelled') return false;
      const d = new Date(s.date);
      return d >= start && d < end;
    })
    .reduce((total, s) => total + getShiftDurationHours(s), 0);
}

/**
 * Check if adding this shift would exceed max weekly hours
 */
export function checkMaxWeeklyHours(staffId, newShift, existingShifts, config = DEFAULT_SCHEDULING_CONFIG) {
  // Find the Monday of the shift's week
  const shiftDate = new Date(newShift.date);
  const day = shiftDate.getDay();
  const mondayOffset = day === 0 ? -6 : 1 - day;
  const monday = new Date(shiftDate);
  monday.setDate(monday.getDate() + mondayOffset);
  const weekStart = monday.toISOString().split('T')[0];

  const currentHours = getWeeklyHours(staffId, weekStart, existingShifts.filter(s => s.id !== newShift.id));
  const newHours = getShiftDurationHours(newShift);
  const totalHours = currentHours + newHours;

  return {
    valid: totalHours <= config.maxWeeklyHours,
    currentHours: Math.round(currentHours * 10) / 10,
    newShiftHours: Math.round(newHours * 10) / 10,
    totalHours: Math.round(totalHours * 10) / 10,
    maxHours: config.maxWeeklyHours,
  };
}

/**
 * Check consecutive working days
 */
export function checkConsecutiveDays(staffId, newShift, existingShifts, config = DEFAULT_SCHEDULING_CONFIG) {
  const shiftDate = new Date(newShift.date);
  const staffShiftDates = new Set(
    existingShifts
      .filter(s => s.assignedStaffId === staffId && s.id !== newShift.id && s.status !== 'cancelled')
      .map(s => s.date)
  );
  staffShiftDates.add(newShift.date);

  // Count consecutive days including the new shift date
  let maxConsecutive = 0;
  let current = 0;
  // Check a 14-day window around the shift date
  for (let i = -7; i <= 7; i++) {
    const d = new Date(shiftDate);
    d.setDate(d.getDate() + i);
    const dateStr = d.toISOString().split('T')[0];
    if (staffShiftDates.has(dateStr)) {
      current++;
      maxConsecutive = Math.max(maxConsecutive, current);
    } else {
      current = 0;
    }
  }

  return {
    valid: maxConsecutive <= config.maxConsecutiveDays,
    consecutiveDays: maxConsecutive,
    maxAllowed: config.maxConsecutiveDays,
  };
}

/**
 * Run ALL validation checks for assigning a staff member to a shift.
 * Returns { valid: boolean, errors: string[], warnings: string[], details: object }
 */
export function validateShiftAssignment(staffId, newShift, existingShifts, staffProfile = {}, config = DEFAULT_SCHEDULING_CONFIG) {
  const errors = [];
  const warnings = [];
  const details = {};

  // 1. Double booking
  const doubleBooking = checkDoubleBooking(staffId, newShift, existingShifts);
  details.doubleBooking = doubleBooking;
  if (doubleBooking.conflict) {
    errors.push(`Double booking: staff already assigned to ${doubleBooking.conflictingShift.startTime}–${doubleBooking.conflictingShift.endTime} on ${newShift.date}`);
  }

  // 2. Rest period
  const restPeriod = checkRestPeriod(staffId, newShift, existingShifts, config);
  details.restPeriod = restPeriod;
  if (!restPeriod.valid) {
    errors.push(`Insufficient rest: only ${restPeriod.restHours}h between shifts (minimum ${restPeriod.required}h required)`);
  }

  // 3. Weekly hours
  const weeklyHours = checkMaxWeeklyHours(staffId, newShift, existingShifts, config);
  details.weeklyHours = weeklyHours;
  if (!weeklyHours.valid) {
    errors.push(`Exceeds weekly hours: ${weeklyHours.totalHours}h total (max ${weeklyHours.maxHours}h)`);
  } else if (weeklyHours.totalHours > weeklyHours.maxHours * 0.9) {
    warnings.push(`Approaching weekly hours limit: ${weeklyHours.totalHours}h of ${weeklyHours.maxHours}h`);
  }

  // 4. Consecutive days
  const consecutive = checkConsecutiveDays(staffId, newShift, existingShifts, config);
  details.consecutiveDays = consecutive;
  if (!consecutive.valid) {
    errors.push(`Too many consecutive days: ${consecutive.consecutiveDays} (max ${consecutive.maxAllowed})`);
  }

  // 5. Shift duration check
  const duration = getShiftDurationHours(newShift);
  details.shiftDuration = duration;
  if (duration > config.maxDailyHours) {
    errors.push(`Shift too long: ${duration}h (max ${config.maxDailyHours}h)`);
  }

  // 6. Role/skill match (if staffProfile has roles/skills)
  if (newShift.requiredRole && staffProfile.roles && !staffProfile.roles.includes(newShift.requiredRole)) {
    warnings.push(`Staff role mismatch: shift requires "${newShift.requiredRole}"`);
  }
  if (newShift.requiredSkills && newShift.requiredSkills.length > 0 && staffProfile.skills) {
    const missing = newShift.requiredSkills.filter(sk => !staffProfile.skills.includes(sk));
    if (missing.length > 0) {
      warnings.push(`Missing skills: ${missing.join(', ')}`);
    }
  }

  // 7. Contract hours check
  if (staffProfile.contractHours && weeklyHours.totalHours > staffProfile.contractHours * 1.1) {
    warnings.push(`Exceeds contract hours: ${weeklyHours.totalHours}h vs ${staffProfile.contractHours}h contract`);
  }

  return {
    valid: errors.length === 0,
    errors,
    warnings,
    details,
  };
}

export function normalizeWeekStartDay(weekStartsOn = 1) {
  const parsed = Number(weekStartsOn);
  if (Number.isNaN(parsed)) return 1;
  return ((parsed % 7) + 7) % 7;
}

/**
 * Get the week-start date string for a given date.
 * `weekStartsOn` follows JS getDay numbering: Sunday=0 ... Saturday=6.
 */
export function getWeekStart(date, weekStartsOn = 1) {
  const d = typeof date === 'string' ? parseLocalDate(date) : new Date(date);
  const day = d.getDay();
  const normalizedWeekStart = normalizeWeekStartDay(weekStartsOn);
  const offset = (day - normalizedWeekStart + 7) % 7;
  d.setDate(d.getDate() - offset);
  return formatLocalDate(d);
}

/**
 * Generate date strings for a week (Mon–Sun)
 */
export function getWeekDates(weekStart) {
  const dates = [];
  const start = parseLocalDate(weekStart);
  for (let i = 0; i < 7; i++) {
    const d = new Date(start);
    d.setDate(d.getDate() + i);
    dates.push(formatLocalDate(d));
  }
  return dates;
}

/**
 * View-range options available in the UI
 */
export const VIEW_RANGES = [
  { key: '1w', labelKey: 'viewRange.1week', days: 7 },
  { key: '2w', labelKey: 'viewRange.2weeks', days: 14 },
  { key: '3w', labelKey: 'viewRange.3weeks', days: 21 },
  { key: '1m', labelKey: 'viewRange.1month', days: null },   // calendar month
  { key: '2m', labelKey: 'viewRange.2months', days: null },
  { key: '3m', labelKey: 'viewRange.3months', days: null },
];

/**
 * Return an array of date-strings for the chosen view range starting from weekStart.
 * For week-based ranges: exact multiples of 7.
 * For month-based ranges: from weekStart to the last day of the Nth month.
 */
export function getDateRange(weekStart, rangeKey = '1w') {
  const start = parseLocalDate(weekStart);
  const range = VIEW_RANGES.find(r => r.key === rangeKey) || VIEW_RANGES[0];

  let totalDays;
  if (range.days) {
    totalDays = range.days;
  } else {
    // month-based: go forward N calendar months from weekStart
    const months = parseInt(rangeKey);
    const end = new Date(start);
    end.setMonth(end.getMonth() + months);
    end.setDate(0); // last day of that month
    totalDays = Math.ceil((end - start) / (1000 * 60 * 60 * 24)) + 1;
    if (totalDays < 28) totalDays = 28; // minimum safety
  }

  const dates = [];
  for (let i = 0; i < totalDays; i++) {
    const d = new Date(start);
    d.setDate(d.getDate() + i);
    dates.push(formatLocalDate(d));
  }
  return dates;
}

/**
 * Split an array of dates into week-chunks of 7 for grid rendering.
 */
export function chunkIntoWeeks(dates) {
  const weeks = [];
  for (let i = 0; i < dates.length; i += 7) {
    weeks.push(dates.slice(i, i + 7));
  }
  return weeks;
}

/**
 * Navigate forward / backward based on view range.
 * Returns new weekStart string.
 */
export function navigateRange(currentWeekStart, direction, rangeKey = '1w') {
  const d = parseLocalDate(currentWeekStart);
  const range = VIEW_RANGES.find(r => r.key === rangeKey) || VIEW_RANGES[0];
  if (range.days) {
    d.setDate(d.getDate() + (direction * range.days));
  } else {
    const months = parseInt(rangeKey);
    d.setMonth(d.getMonth() + (direction * months));
  }
  return formatLocalDate(d);
}

// ═══════════════════════════════════════════════════════════════════════════
// Shift-block & sector-specific extensions
// ═══════════════════════════════════════════════════════════════════════════

/**
 * Sector-specific scheduling config overrides
 */
export const SECTOR_SCHEDULING_CONFIG = {
  general: { ...DEFAULT_SCHEDULING_CONFIG },
  care: {
    ...DEFAULT_SCHEDULING_CONFIG,
    maxDailyHours: 14.5,      // Long days common in care
    allowSleepIns: true,
    sleepInCountsAsRest: true, // Sleep-in can count towards rest period
    maxConsecutiveNights: 4,
  },
  hospitality: {
    ...DEFAULT_SCHEDULING_CONFIG,
    maxDailyHours: 12,
    allowSplitShifts: true,
    minSplitGapHours: 2,      // Minimum gap in split shift
    maxSplitGapHours: 6,      // Maximum gap in split shift
  },
  security: {
    ...DEFAULT_SCHEDULING_CONFIG,
    maxDailyHours: 16,         // Security does longer shifts
    maxWeeklyHours: 60,        // Often WTD opt-out
    minRestHoursBetweenShifts: 8,
    allowOnCall: true,
  },
  healthcare: {
    ...DEFAULT_SCHEDULING_CONFIG,
    maxDailyHours: 13,
    maxConsecutiveDays: 5,
    requireSupervisor: true,
    maxConsecutiveNights: 3,
  },
  retail: {
    ...DEFAULT_SCHEDULING_CONFIG,
    maxDailyHours: 10,
    maxWeeklyHours: 40,
  },
};

/**
 * Get config for a specific sector (falls back to general)
 */
export function getSectorConfig(sector, customOverrides = {}) {
  const base = SECTOR_SCHEDULING_CONFIG[sector] || SECTOR_SCHEDULING_CONFIG.general;
  const nestedOverrides = customOverrides?.customOverrides || {};
  return {
    ...base,
    ...nestedOverrides,
    ...customOverrides,
    customOverrides: nestedOverrides,
  };
}

/**
 * Calculate total working hours for a shift block (multi-segment)
 */
export function getBlockDurationHours(shift) {
  if (shift.segments && shift.segments.length > 0) {
    return getBlockWorkingHours(shift.segments, false);
  }
  return getShiftDurationHours(shift);
}

/**
 * Validate an entire shift block (multi-segment)
 * Checks segment continuity, break requirements, and total duration
 */
export function validateShiftBlock(shiftBlock, config = DEFAULT_SCHEDULING_CONFIG) {
  const errors = [];
  const warnings = [];

  if (!shiftBlock.segments || shiftBlock.segments.length === 0) {
    // Legacy single-segment, use standard duration check
    const duration = getShiftDurationHours(shiftBlock);
    if (duration > config.maxDailyHours) {
      errors.push(`Shift too long: ${duration}h (max ${config.maxDailyHours}h)`);
    }
    return { valid: errors.length === 0, errors, warnings };
  }

  // 1. Validate segment continuity for linked blocks
  if (shiftBlock.blockType === BLOCK_TYPES.LINKED) {
    const continuity = validateSegmentContinuity(shiftBlock.segments);
    if (!continuity.valid) {
      errors.push(...continuity.errors);
    }
  }

  // 2. Total working hours (excluding breaks and sleep-ins where configured)
  const workingHours = getBlockWorkingHours(
    shiftBlock.segments,
    config.sleepInCountsAsWorkingTime === true,
  );
  if (workingHours > config.maxDailyHours) {
    errors.push(`Total working hours ${workingHours.toFixed(1)}h exceeds max ${config.maxDailyHours}h`);
  }

  // 3. Break requirement: if working > breakThresholdHours, need a break segment
  if (config.enforceBreaks && workingHours > config.breakThresholdHours) {
    const hasBreak = shiftBlock.segments.some(s => s.type === SEGMENT_TYPES.BREAK);
    if (!hasBreak) {
      warnings.push(`Working ${workingHours.toFixed(1)}h without a break — a ${config.breakDurationMinutes}min break is recommended after ${config.breakThresholdHours}h`);
    }
  }

  // 4. Split shift validation
  if (shiftBlock.blockType === BLOCK_TYPES.SPLIT) {
    if (!config.allowSplitShifts) {
      errors.push('Split shifts are not allowed in current configuration');
    } else {
      // Check gap between split parts
      const sortedSegments = [...shiftBlock.segments]
        .filter(s => s.type !== SEGMENT_TYPES.BREAK)
        .sort((a, b) => timeToMinutes(a.startTime) - timeToMinutes(b.startTime));

      for (let i = 1; i < sortedSegments.length; i++) {
        const prevEnd = timeToMinutes(sortedSegments[i - 1].endTime);
        const nextStart = timeToMinutes(sortedSegments[i].startTime);
        const gapHours = (nextStart - prevEnd) / 60;

        if (config.minSplitGapHours && gapHours < config.minSplitGapHours) {
          warnings.push(`Split gap of ${gapHours.toFixed(1)}h is less than recommended ${config.minSplitGapHours}h`);
        }
        if (config.maxSplitGapHours && gapHours > config.maxSplitGapHours) {
          warnings.push(`Split gap of ${gapHours.toFixed(1)}h exceeds maximum ${config.maxSplitGapHours}h`);
        }
      }
    }
  }

  // 5. Sleep-in specific validation
  const sleepInSegments = shiftBlock.segments.filter(s => s.type === SEGMENT_TYPES.SLEEP_IN);
  if (sleepInSegments.length > 0) {
    if (!config.allowSleepIns) {
      errors.push('Sleep-in shifts are not enabled for this sector');
    }
    for (const seg of sleepInSegments) {
      const segHours = (timeToMinutes(seg.endTime) + (timeToMinutes(seg.endTime) <= timeToMinutes(seg.startTime) ? 1440 : 0) - timeToMinutes(seg.startTime)) / 60;
      if (segHours < 6) warnings.push('Sleep-in segment is less than 6 hours');
      if (segHours > 10) warnings.push('Sleep-in segment exceeds 10 hours');
    }
  }

  // 6. On-call specific validation
  const onCallSegments = shiftBlock.segments.filter(s => s.type === SEGMENT_TYPES.ON_CALL);
  if (onCallSegments.length > 0 && !config.allowOnCall) {
    warnings.push('On-call segments not standard for this sector');
  }

  return { valid: errors.length === 0, errors, warnings };
}

/**
 * Extended validation for shift blocks — wraps validateShiftAssignment
 * with block-aware checks
 */
export function validateBlockAssignment(staffId, shiftBlock, existingShifts, staffProfile = {}, config = DEFAULT_SCHEDULING_CONFIG) {
  // First validate the block itself
  const blockValidation = validateShiftBlock(shiftBlock, config);

  // Convert to a simple shift shape for the base validator
  const span = shiftBlock.segments && shiftBlock.segments.length > 0
    ? getBlockTimeSpan(shiftBlock.segments)
    : { startTime: shiftBlock.startTime, endTime: shiftBlock.endTime };

  const pseudoShift = {
    ...shiftBlock,
    startTime: span.startTime,
    endTime: span.endTime,
  };

  // Run standard assignment validation
  const assignmentValidation = validateShiftAssignment(staffId, pseudoShift, existingShifts, staffProfile, config);

  return {
    valid: blockValidation.valid && assignmentValidation.valid,
    errors: [...blockValidation.errors, ...assignmentValidation.errors],
    warnings: [...blockValidation.warnings, ...assignmentValidation.warnings],
    details: {
      blockValidation,
      assignmentValidation: assignmentValidation.details,
    },
  };
}

/**
 * Check consecutive night shifts specifically
 * Many sectors limit consecutive nights (e.g., 3–4 max)
 */
export function checkConsecutiveNights(staffId, newShift, existingShifts, config = DEFAULT_SCHEDULING_CONFIG) {
  const maxNights = config.maxConsecutiveNights || 7;
  const shiftDate = new Date(newShift.date);

  // Determine if this is a night shift (starts after 20:00 or ends before 06:00 next day)
  const startMin = timeToMinutes(newShift.startTime || (newShift.segments?.[0]?.startTime));
  const isNight = startMin >= 20 * 60 || startMin < 6 * 60;
  if (!isNight) return { valid: true, consecutiveNights: 0, maxAllowed: maxNights };

  const nightDates = new Set();
  nightDates.add(newShift.date);

  for (const s of existingShifts) {
    if (s.assignedStaffId !== staffId || s.id === newShift.id || s.status === 'cancelled') continue;
    const sStart = timeToMinutes(s.startTime || (s.segments?.[0]?.startTime));
    if (sStart >= 20 * 60 || sStart < 6 * 60) {
      nightDates.add(s.date);
    }
  }

  // Count consecutive nights around the new shift date
  let maxConsecutive = 0;
  let current = 0;
  for (let i = -7; i <= 7; i++) {
    const d = new Date(shiftDate);
    d.setDate(d.getDate() + i);
    const dateStr = d.toISOString().split('T')[0];
    if (nightDates.has(dateStr)) {
      current++;
      maxConsecutive = Math.max(maxConsecutive, current);
    } else {
      current = 0;
    }
  }

  return {
    valid: maxConsecutive <= maxNights,
    consecutiveNights: maxConsecutive,
    maxAllowed: maxNights,
  };
}

const schedulingEngine = {
  DEFAULT_SCHEDULING_CONFIG,
  SECTOR_SCHEDULING_CONFIG,
  getSectorConfig,
  timeToMinutes,
  minutesToTime,
  getShiftDurationHours,
  getBlockDurationHours,
  timesOverlap,
  checkDoubleBooking,
  checkRestPeriod,
  getWeeklyHours,
  checkMaxWeeklyHours,
  checkConsecutiveDays,
  checkConsecutiveNights,
  validateShiftAssignment,
  validateShiftBlock,
  validateBlockAssignment,
  getWeekStart,
  getWeekDates,
  VIEW_RANGES,
  getDateRange,
  chunkIntoWeeks,
  navigateRange,
};
export default schedulingEngine;
